import { useCallback, useMemo } from 'react';
import type { RentalGateway, RentalItem, RentalOrder, RentalStatus } from '@forq/rental-data';
import type { ResolvedPeriod } from './period';
import { ReportActions, type SectionReport } from './report';
import { CustomizableMetrics } from '@forq/ui';
import { Bars, euro0, Kpi, KpiGrid, num, Panel, SectionShell, useAsyncData } from './shared';

const statusOrder: readonly RentalStatus[] = ['request', 'reserved', 'active', 'return_scheduled', 'completed', 'cancelled'];
const statusLabels: Readonly<Record<RentalStatus, string>> = {
  request: 'Anfrage',
  reserved: 'Reserviert',
  active: 'Vermietet',
  return_scheduled: 'Rückholung geplant',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

function monthlyEquivalent(item: RentalItem): number {
  if (item.monthlyRate > 0) return item.monthlyRate;
  if (item.weeklyRate > 0) return item.weeklyRate * 4.33;
  return item.dailyRate * 30;
}

/** Mietpark-Auswertung: aktuelle Auslastung (Momentaufnahme) + neue Verträge im gewählten Zeitraum. */
export function RentalSection({ tenantId, gateway, period, tenantName }: {
  readonly tenantId: string;
  readonly gateway: RentalGateway;
  readonly period: ResolvedPeriod;
  readonly tenantName: string;
}) {
  const load = useCallback((): Promise<readonly RentalOrder[]> => gateway.listRentalOrders(tenantId), [gateway, tenantId]);
  const { data, loading, error, reload } = useAsyncData(load);

  const summary = useMemo(() => {
    if (!data) return null;
    const inPeriod = (iso: string) => { const day = iso.slice(0, 10); return (period.from === null || day >= period.from) && day <= period.to; };
    const counts = new Map<RentalStatus, number>();
    const machinesOnRent = new Set<string>();
    let monthlyRunRate = 0;
    let openBilling = 0;
    let newInPeriod = 0;
    for (const order of data) {
      counts.set(order.status, (counts.get(order.status) ?? 0) + 1);
      if (order.status !== 'cancelled' && order.billingStatus !== 'billed') openBilling += 1;
      if (inPeriod(order.createdAt)) newInPeriod += 1;
      if (order.status === 'active') {
        for (const item of order.items) {
          machinesOnRent.add(item.machineNumber);
          monthlyRunRate += monthlyEquivalent(item);
        }
      }
    }
    return { total: data.length, counts, activeCount: counts.get('active') ?? 0, machinesOnRent: machinesOnRent.size, monthlyRunRate, openBilling, newInPeriod };
  }, [data, period]);

  const report: SectionReport | null = useMemo(() => {
    if (!summary) return null;
    return {
      title: 'Mietpark',
      kpis: [
        { label: 'Aktive Vermietungen', value: num(summary.activeCount) },
        { label: 'Maschinen vermietet', value: num(summary.machinesOnRent) },
        { label: 'Mietumsatz / Monat (lfd.)', value: euro0(summary.monthlyRunRate) },
        { label: 'Offene Abrechnung', value: num(summary.openBilling) },
        { label: 'Neue Verträge (Zeitraum)', value: num(summary.newInPeriod) },
      ],
      blocks: [
        { heading: 'Mietaufträge nach Status', columns: ['Status', 'Anzahl'], rows: statusOrder.map((status) => [statusLabels[status], summary.counts.get(status) ?? 0]) },
      ],
    };
  }, [summary]);

  return (
    <SectionShell loading={loading} error={error} onReload={reload}>
      {summary && report ? (
        <div className="auswertung__stack">
          <div className="auswertung__sectionhead">
            <span className="sales-eyebrow">Mietpark · Stand jetzt (neue Verträge: {period.label})</span>
            <ReportActions report={report} period={period} tenantName={tenantName} />
          </div>
          <KpiGrid>
            <CustomizableMetrics
              pageKey="auswertung-miete"
              tiles={[
                { key: 'active', node: <Kpi label="Aktive Vermietungen" value={num(summary.activeCount)} hint={`${num(summary.total)} Mietaufträge gesamt`} /> },
                { key: 'machines', node: <Kpi label="Maschinen vermietet" value={num(summary.machinesOnRent)} hint="aktuell beim Kunden" /> },
                { key: 'runrate', node: <Kpi label="Mietumsatz / Monat (lfd.)" value={euro0(summary.monthlyRunRate)} hint="Run-Rate aktiver Verträge" /> },
                { key: 'openbilling', node: <Kpi label="Offene Abrechnung" value={num(summary.openBilling)} hint="noch nicht fakturiert" /> },
                { key: 'new', node: <Kpi label="Neue Verträge (Zeitraum)" value={num(summary.newInPeriod)} hint={period.label} /> },
              ]}
            />
          </KpiGrid>

          <Panel title="Mietaufträge nach Status">
            <Bars
              rows={statusOrder.map((status) => ({ key: status, label: statusLabels[status], value: summary.counts.get(status) ?? 0 })).filter((row) => row.value > 0)}
              emptyLabel="Noch keine Mietaufträge."
              format={num}
            />
          </Panel>
        </div>
      ) : null}
    </SectionShell>
  );
}
